import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonText } from '../../components/Skeleton';
import { AuditSummaryText } from './presentation';
import { useModuleLabel } from './useModuleLabel';
import { useAuditEntry } from './queries';

/**
 * Ein Protokolleintrag in voller Laenge -- F5-02, Anforderung 1.
 *
 * **Hier stehen die Werte, in der Liste nicht.** Der Unterschied ist keine
 * Platzfrage: Eine Liste, die alte und neue Werte offen zeigt, ist ein
 * Personenverzeichnis, das sich mit einem Blick ueberfliegen laesst. Wer
 * einen einzelnen Vorgang aufschlaegt, hat einen Grund dafuer -- und dieser
 * Aufruf steht seinerseits im Protokoll.
 *
 * **Redigierte Felder erscheinen mit Namen und ohne Wert.** Dass jemand sein
 * Kennwort geaendert hat, ist protokollpflichtig; womit, nie. Das ist keine
 * Entscheidung dieser Maske, sondern eine der Protokollierung
 * (`core/audit.py`, `REDACTED_COLUMNS`) -- hier wird sie nur sichtbar.
 *
 * **Vorgangskennung und Adresse stehen unten.** Sie sind die beiden Angaben,
 * mit denen sich ein Eintrag im Serverprotokoll wiederfinden laesst -- die
 * Frage, die bei einer Pruefung als zweite kommt.
 */
export function AuditDetail({ entryId, onClose }: { entryId: string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const moduleLabel = useModuleLabel();
  const entry = useAuditEntry(entryId);

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="dialog__overlay" />
        <Dialog.Content className="card dialog__content">
          <Dialog.Title asChild>
            <h2 style={{ marginBottom: 'var(--space-4)' }}>{t('audit.detailTitle')}</h2>
          </Dialog.Title>
          <Dialog.Description className="visually-hidden">
            {t('audit.detailSubtitle')}
          </Dialog.Description>

          {entry.isPending ? <SkeletonText lines={4} /> : null}
          {entry.isError ? <ErrorNotice error={entry.error} /> : null}

          {entry.data ? (
            <div className="stack">
              <dl className="definition-list">
                <dt>{t('audit.occurredAt')}</dt>
                <dd>{new Date(entry.data.occurred_at).toLocaleString(i18n.language)}</dd>
                <dt>{t('audit.actor')}</dt>
                <dd>
                  {entry.data.actor_name ??
                    t(`history.actor.${entry.data.actor_type}`, {
                      defaultValue: entry.data.actor_type,
                    })}{' '}
                  (
                  {t(`history.actor.${entry.data.actor_type}`, {
                    defaultValue: entry.data.actor_type,
                  })}
                  )
                </dd>
                <dt>{t('audit.module')}</dt>
                <dd>{moduleLabel(entry.data.module)}</dd>
                <dt>{t('audit.object')}</dt>
                <dd>
                  {entry.data.object_label ?? entry.data.object_id ?? t('app.none')}{' '}
                  <span className="table__muted">({entry.data.object_type})</span>
                </dd>
                <dt>{t('audit.summary')}</dt>
                <dd>
                  <AuditSummaryText summary={entry.data.summary} />
                </dd>
              </dl>

              {entry.data.changes.length > 0 ? (
                <div className="table-wrapper">
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">{t('audit.field')}</th>
                        <th scope="col">{t('audit.oldValue')}</th>
                        <th scope="col">{t('audit.newValue')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entry.data.changes.map((change) => (
                        <tr key={change.field}>
                          <td>{t(`auditField.${change.field}`, { defaultValue: change.field })}</td>
                          {change.redacted ? (
                            <td colSpan={2} className="table__muted">
                              {t('audit.redacted')}
                            </td>
                          ) : (
                            <>
                              <td className="table__muted">
                                {change.old ?? t('audit.emptyValue')}
                              </td>
                              <td>{change.new ?? t('audit.emptyValue')}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="table__muted">{t('audit.noChanges')}</p>
              )}

              <dl className="definition-list">
                <dt>{t('audit.requestId')}</dt>
                <dd className="table__muted">{entry.data.request_id ?? t('app.none')}</dd>
                <dt>{t('audit.ipAddress')}</dt>
                <dd className="table__muted">{entry.data.ip_address ?? t('app.none')}</dd>
              </dl>
            </div>
          ) : null}

          <div className="row">
            <Button variant="secondary" onClick={onClose}>
              {t('app.close')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
